import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanMatchFn, Router, UrlSegment, UrlTree } from '@angular/router';
import { filter, map, take, tap } from 'rxjs';
import { ROUTE_PATH } from '../constants';
import { EopRoute } from '../interfaces';
import { LogCat, LogLevel } from '../models/logger';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { LoggerService } from '../services/logger.service';
import { TermsService } from '../services/terms.service';
import { userHasAccess } from '../utils/auth.utils';

export type GuardDecisionReason =
	| 'not-authenticated'
	| 'account-info-error'
	| 'no-access'
	| 'no-access-to-root'
	| 'terms-not-accepted';

type GuardDecision =
	| { kind: 'allow' }
	| { kind: 'redirect'; urlTree: UrlTree; reason: GuardDecisionReason }
	| { kind: 'skip'; reason: 'config-mismatch' };

type Deps = { auth: AuthService; config: ConfigService; terms: TermsService; router: Router };

/**
 * Unified access policy guard combining:
 * - Authentication state
 * - Account-based authorization (roles / permissions / orgs)
 * - Config gating rules
 * - Terms acceptance (via TermsService)
 */
export const accessGuard: CanMatchFn = (route: EopRoute, segments: UrlSegment[]) => {
	const auth = inject(AuthService);
	const logger = inject(LoggerService);
	const router = inject(Router);
	const config = inject(ConfigService);
	const terms = inject(TermsService);

	return toObservable(auth.authReady).pipe(
		filter((ready) => ready),
		take(1),
		map(() => decide(route, segments, { auth, router, config, terms })),
		map((decision) => translateGuardDecision(decision)),
		tap((result) => logGuardResult(result.decision, joinSegments(segments), logger)),
		map((result) => result.value),
	);
};

const decide = (route: EopRoute, segments: UrlSegment[], deps: Deps): GuardDecision => {
	const { auth, config, terms } = deps;

	// Authentication checks
	if (!auth.authenticated()) {
		const reason: GuardDecisionReason = 'not-authenticated';
		const urlTree = deps.router.createUrlTree(['/', ROUTE_PATH.signIn], {
			queryParams: { redirectReason: reason },
		});
		return { kind: 'redirect', urlTree, reason };
	}

	if (auth.accountInfoError()) {
		const reason: GuardDecisionReason = 'account-info-error';
		const urlTree = deps.router.createUrlTree(['/', ROUTE_PATH.signIn], {
			queryParams: { redirectReason: reason },
		});
		return { kind: 'redirect', urlTree, reason };
	}

	// Skip other customer variants so the router can match the same public path.
	if (route.data?.config && route.data.config !== config.configKey()) {
		return { kind: 'skip', reason: 'config-mismatch' };
	}

	// Authorization checks
	const canAccessRoute = userHasAccess(auth.accountInfo(), {
		permissions: route.data?.access?.permissions ?? [],
		roles: route.data?.access?.roles ?? [],
		operator: route.data?.access?.operator,
	});

	// Check if at root url
	const isRoot = !segments.length;

	if (!canAccessRoute && !isRoot) {
		const urlTree = deps.router.createUrlTree(['/']);
		return { kind: 'redirect', urlTree, reason: 'no-access' };
	}

	if (!canAccessRoute && isRoot) {
		const reason: GuardDecisionReason = 'no-access-to-root';
		const urlTree = deps.router.createUrlTree(['/', ROUTE_PATH.signIn], {
			queryParams: { redirectReason: reason },
		});
		return { kind: 'redirect', urlTree, reason };
	}

	// Terms acceptance gating
	if (!terms.termsAccepted()) {
		const pendingRoute = terms.isAcceptancePending() ? ROUTE_PATH.termsPending : ROUTE_PATH.terms;
		const urlTree = deps.router.createUrlTree(['/', pendingRoute]);
		return { kind: 'redirect', urlTree, reason: 'terms-not-accepted' };
	}

	return { kind: 'allow' };
};

const joinSegments = (segments: UrlSegment[]) => '/' + segments.map((s) => s.path).join('/');

const translateGuardDecision = (
	decision: GuardDecision,
): {
	decision: GuardDecision;
	value: boolean | UrlTree;
} => {
	switch (decision.kind) {
		case 'allow':
			return { decision, value: true };
		case 'skip':
			return { decision, value: false };
		case 'redirect':
			return { decision, value: decision.urlTree };
	}
};

const logGuardResult = (decision: GuardDecision, path: string, logger: LoggerService) => {
	switch (decision.kind) {
		case 'allow':
			logger.log(`Allowed: ${path}`, LogCat.guard, {
				level: LogLevel.success,
			});
			break;
		case 'skip':
			logger.log(`Skipped: ${path} | ${decision.reason}`, LogCat.guard, {
				level: LogLevel.debug,
			});
			break;
		case 'redirect':
			logger.log(
				`Redirect: ${path} -> ${decision.urlTree.toString()} | ${decision.reason}`,
				LogCat.guard,
				{
					level: LogLevel.info,
				},
			);
			break;
	}
};
